
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Search, User, Menu, X, Check, AlertTriangle, Info, Clock, UserX, LogOut, Settings, ChevronDown, FileText, Users, Monitor, Calendar, ClipboardList } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Notification } from '../types';
import { useAppContext } from '../context/AppContext';
import authService from '../services/authService';
import { employeeService } from '../services';

interface SearchResult {
  type: 'employee' | 'page';
  id?: string;
  title: string;
  subtitle?: string;
  path: string;
  icon: React.ReactNode;
}

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { notifications, markAllNotificationsRead, markNotificationRead } = useAppContext();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [toast, setToast] = useState<Notification | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastNotifId = useRef<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Get current user from auth service
  const currentUser = authService.getStoredUser();

  const getUserDisplayName = () => {
    if (currentUser?.fullName && currentUser.fullName.trim()) {
      return currentUser.fullName;
    }
    if (currentUser?.firstName || currentUser?.lastName) {
      return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
    }
    return currentUser?.username || 'Kullanıcı';
  };

  const getUserRole = () => {
    if (currentUser?.roles && currentUser.roles.length > 0) {
      const roleMap: Record<string, string> = {
        'Admin': 'Yönetici',
        'Manager': 'Müdür',
        'HR': 'İK Uzmanı',
        'Employee': 'Çalışan'
      };
      return roleMap[currentUser.roles[0]] || currentUser.roles[0];
    }
    return 'Kullanıcı';
  };

  const getUserInitials = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName[0]}${currentUser.lastName[0]}`.toUpperCase();
    }
    if (currentUser?.fullName && currentUser.fullName.trim()) {
      const parts = currentUser.fullName.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return currentUser.fullName.substring(0, 2).toUpperCase();
    }
    return currentUser?.username?.substring(0, 2).toUpperCase() || 'U';
  };

  // Derive title from path with Turkish translations
  const getTitle = () => {
    const path = location.pathname.substring(1);
    if (!path) return 'Operasyonel Panel';
    const titles: Record<string, string> = {
      'personnel': 'Kullanıcı Yönetimi',
      'organization': 'Organizasyon Şeması',
      'locations': 'Lokasyon Yönetimi',
      'devices': 'PDKS Terminalleri',
      'shifts': 'Vardiya Planlama',
      'payroll': 'Maaş ve Bordro',
      'reports': 'Raporlar ve Analiz',
      'leaves': 'İzin Talepleri',
      'holidays': 'Resmi Tatiller',
      'profile': 'Kullanıcı Profili',
      'settings': 'Sistem Ayarları'
    };
    return titles[path] || 'Panel';
  };

  // Pages for search
  const searchablePages: SearchResult[] = [
    { type: 'page', title: 'Gösterge Paneli', subtitle: 'Ana sayfa', path: '/', icon: <Monitor size={16} className="text-blue-500" /> },
    { type: 'page', title: 'Personel Listesi', subtitle: 'Çalışan yönetimi', path: '/employees', icon: <Users size={16} className="text-emerald-500" /> },
    { type: 'page', title: 'PDKS Cihazları', subtitle: 'Terminal yönetimi', path: '/devices', icon: <Monitor size={16} className="text-purple-500" /> },
    { type: 'page', title: 'Giriş/Çıkış Kayıtları', subtitle: 'Yoklama kayıtları', path: '/attendance-logs', icon: <ClipboardList size={16} className="text-orange-500" /> },
    { type: 'page', title: 'Puantaj Hesaplama', subtitle: 'Aylık puantaj', path: '/timesheet', icon: <Calendar size={16} className="text-cyan-500" /> },
    { type: 'page', title: 'Bordro ve Maaş', subtitle: 'Maaş hesaplama', path: '/payroll', icon: <FileText size={16} className="text-green-500" /> },
    { type: 'page', title: 'Raporlar', subtitle: 'Raporlar ve analiz', path: '/reports', icon: <FileText size={16} className="text-indigo-500" /> },
    { type: 'page', title: 'İzin Talepleri', subtitle: 'İzin yönetimi', path: '/leaves', icon: <Calendar size={16} className="text-amber-500" /> },
    { type: 'page', title: 'Resmi Tatiller', subtitle: 'Tatil takvimi', path: '/holidays', icon: <Calendar size={16} className="text-red-500" /> },
    { type: 'page', title: 'Vardiya Planlama', subtitle: 'Vardiya yönetimi', path: '/shifts', icon: <Clock size={16} className="text-teal-500" /> },
    { type: 'page', title: 'Organizasyon Şeması', subtitle: 'Departman yapısı', path: '/organization', icon: <Users size={16} className="text-violet-500" /> },
    { type: 'page', title: 'Lokasyonlar', subtitle: 'Lokasyon yönetimi', path: '/locations', icon: <Monitor size={16} className="text-rose-500" /> },
    { type: 'page', title: 'Ayarlar', subtitle: 'Sistem ayarları', path: '/settings', icon: <Settings size={16} className="text-slate-500" /> },
    { type: 'page', title: 'Veri Aktarımı', subtitle: 'Excel import', path: '/import', icon: <FileText size={16} className="text-emerald-500" /> },
    { type: 'page', title: 'Kullanıcı Yönetimi', subtitle: 'Sistem kullanıcıları', path: '/users', icon: <User size={16} className="text-blue-500" /> },
  ];

  // Search handler with debounce
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const q = query.toLowerCase().trim();
    setSearchLoading(true);
    setShowSearchResults(true);

    // Search pages
    const pageResults = searchablePages.filter(p =>
      p.title.toLowerCase().includes(q) ||
      (p.subtitle && p.subtitle.toLowerCase().includes(q))
    );

    // Search employees from API
    let employeeResults: SearchResult[] = [];
    try {
      const response = await employeeService.getPaged(1, 10, query);
      const empList = response.data?.data || response.data?.items || (Array.isArray(response.data) ? response.data : []);
      if (empList.length > 0) {
        employeeResults = empList.map((emp: any) => ({
          type: 'employee' as const,
          id: String(emp.id),
          title: emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
          subtitle: [emp.departmentName, emp.positionName].filter(Boolean).join(' - ') || '',
          path: `/employee-stats?id=${emp.id}`,
          icon: <User size={16} className="text-blue-500" />
        }));
      }
    } catch (error) {
      console.error('Personel arama hatası:', error);
    }

    setSearchResults([...pageResults.slice(0, 5), ...employeeResults.slice(0, 5)]);
    setSearchLoading(false);
  }, []);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleSearchResultClick = (result: SearchResult) => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    navigate(result.path);
  };

  // Notification navigation based on title/type
  const getNotificationRoute = (n: Notification): string | null => {
    if (n.relatedEntityType) {
      switch (n.relatedEntityType) {
        case 'Leave': return '/leaves';
        case 'Device': return '/devices';
        case 'Employee': return '/personnel';
        case 'Attendance': return '/attendance-logs';
        default: break;
      }
    }
    // Fallback: derive from title
    const title = n.title.toLowerCase();
    if (title.includes('izin')) return '/leaves';
    if (title.includes('cihaz') || title.includes('zkteco') || title.includes('terminal')) return '/devices';
    if (title.includes('devamsızlık') || title.includes('devamsizlik')) return '/attendance-logs';
    if (title.includes('geç') || title.includes('gec')) return '/attendance-logs';
    if (title.includes('personel') || title.includes('çalışan')) return '/personnel';
    return null;
  };

  const handleNotificationClick = (n: Notification) => {
    markNotificationRead(n.id);
    const route = getNotificationRoute(n);
    if (route) {
      setShowNotifications(false);
      navigate(route);
    }
  };

  // Only show unread notifications in the dropdown
  const unreadNotifications = notifications.filter(n => !n.read);
  const unreadCount = unreadNotifications.length;

  // Sync toast with the latest notification
  useEffect(() => {
    const latest = notifications[0];
    if (latest && latest.id !== lastNotifId.current) {
      setToast(latest);
      lastNotifId.current = latest.id;

      const timer = setTimeout(() => {
        setToast(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [notifications]);

  const getIcon = (type: string, title: string = '') => {
    if (title.includes('Geç')) return <Clock size={16} className="text-amber-500" />;
    if (title.includes('Erken')) return <LogOut size={16} className="text-amber-500" />;
    if (title.includes('Devamsızlık')) return <UserX size={16} className="text-red-500" />;

    switch (type) {
      case 'ERROR': return <AlertTriangle size={16} className="text-red-500" />;
      case 'WARNING': return <AlertTriangle size={16} className="text-amber-500" />;
      default: return <Info size={16} className="text-blue-500" />;
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error);
      navigate('/login');
    }
  };

  return (
    <>
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8 sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-slate-800">{getTitle()}</h2>
        </div>

        <div className="flex items-center gap-6">
          {/* Search Bar */}
          <div className="relative hidden md:block" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Sistemde ara..."
              value={searchQuery}
              onChange={onSearchChange}
              onFocus={() => { if (searchQuery.trim()) setShowSearchResults(true); }}
              className="pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            {showSearchResults && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 max-h-96 overflow-y-auto">
                {searchLoading ? (
                  <div className="p-4 text-center text-slate-400 text-sm">Aranıyor...</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-sm">Sonuç bulunamadı</div>
                ) : (
                  <>
                    {searchResults.filter(r => r.type === 'page').length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50">Sayfalar</div>
                        {searchResults.filter(r => r.type === 'page').map((result, i) => (
                          <button
                            key={`page-${i}`}
                            onClick={() => handleSearchResultClick(result)}
                            className="w-full px-3 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors"
                          >
                            {result.icon}
                            <div>
                              <p className="text-sm font-medium text-slate-800">{result.title}</p>
                              {result.subtitle && <p className="text-xs text-slate-400">{result.subtitle}</p>}
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    {searchResults.filter(r => r.type === 'employee').length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50">Personel</div>
                        {searchResults.filter(r => r.type === 'employee').map((result, i) => (
                          <button
                            key={`emp-${i}`}
                            onClick={() => handleSearchResultClick(result)}
                            className="w-full px-3 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors"
                          >
                            {result.icon}
                            <div>
                              <p className="text-sm font-medium text-slate-800">{result.title}</p>
                              {result.subtitle && <p className="text-xs text-slate-400">{result.subtitle}</p>}
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative p-2 rounded-lg transition-colors ${showNotifications ? 'bg-slate-100 text-blue-600' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[8px] font-black flex items-center justify-center rounded-full ring-2 ring-white animate-pulse">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                  <h3 className="font-bold text-slate-700 text-xs uppercase tracking-widest">Bildirimler</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllNotificationsRead} className="text-[10px] text-blue-600 font-bold hover:underline flex items-center gap-1">
                      <Check size={12} /> Tümünü Okundu İşaretle
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto custom-scrollbar">
                  {unreadNotifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm italic">Okunmamış bildirim yok</div>
                  ) : (
                    unreadNotifications.map(n => (
                      <div
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className="p-4 border-b border-slate-50 cursor-pointer transition-colors hover:bg-blue-50/40"
                      >
                        <div className="flex gap-4">
                          <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                            n.type === 'ERROR' ? 'bg-red-50 text-red-500' :
                            n.type === 'WARNING' ? 'bg-amber-50 text-amber-500' :
                            'bg-blue-50 text-blue-500'
                          }`}>
                            {getIcon(n.type, n.title)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-2 uppercase">
                              {new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          {getNotificationRoute(n) && (
                            <div className="shrink-0 text-slate-300">
                              <ChevronDown size={14} className="-rotate-90" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu Dropdown */}
          <div className="relative pl-6 border-l border-slate-200" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="text-right hidden md:block">
                <div className="text-sm font-bold text-slate-800">{getUserDisplayName()}</div>
                <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">{getUserRole()}</div>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-black border-2 border-white shadow-lg">
                {getUserInitials()}
              </div>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                {/* User Info Header */}
                <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                  <p className="text-sm font-bold text-slate-800">{getUserDisplayName()}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{currentUser?.email || ''}</p>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/profile');
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <User size={16} className="text-slate-400" />
                    Profilim
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/settings');
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <Settings size={16} className="text-slate-400" />
                    Sistem Ayarları
                  </button>
                </div>

                {/* Logout */}
                <div className="border-t border-slate-100 py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <LogOut size={16} />
                    Çıkış Yap
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-white border border-slate-200 shadow-2xl rounded-2xl p-5 animate-in slide-in-from-right-10 duration-500 z-50 max-w-sm flex items-start gap-4">
           <div className={`shrink-0 p-3 rounded-xl ${
             toast.type === 'ERROR' ? 'bg-red-50 text-red-600' :
             toast.type === 'WARNING' ? 'bg-amber-50 text-amber-600' :
             'bg-blue-50 text-blue-600'
           }`}>
              {getIcon(toast.type, toast.title)}
           </div>
           <div className="flex-1">
              <h4 className="font-bold text-slate-800 text-sm tracking-tight">{toast.title}</h4>
              <p className="text-sm text-slate-500 mt-1 leading-snug">{toast.message}</p>
              <p className="text-[10px] text-slate-400 mt-3 font-bold uppercase">Az önce</p>
           </div>
           <button onClick={() => setToast(null)} className="text-slate-300 hover:text-slate-600 p-1 transition-colors">
             <X size={18} />
           </button>
        </div>
      )}
    </>
  );
};

export default Header;
